/**
 * ErrorFallback - React Error Boundary fallback UI
 *
 * Full-page error display shown when React Error Boundary catches an error.
 * Provides user-friendly error message with retry functionality and navigation.
 *
 * WHY THIS COMPONENT:
 * - Prevents white screen of death when errors occur
 * - Provides graceful degradation and recovery path
 * - Context-aware retry button (payment vs general errors)
 * - Maintains brand aesthetic even in error state
 *
 * WHY full-page design:
 * - Errors that trigger Error Boundary are usually fatal for current view
 * - Need to block all interaction and provide clear recovery path
 * - Full-page design ensures user attention on error message
 *
 * Accessibility:
 * - Back button with aria-label for screen readers
 * - Warning icon with aria-hidden (decorative)
 * - Semantic heading structure
 * - Clear, actionable error message
 *
 * USAGE:
 * Used as fallback component in React Error Boundary:
 * <ErrorBoundary FallbackComponent={ErrorFallback}>
 *   <App />
 * </ErrorBoundary>
 *
 * Shows when:
 * - Unhandled JavaScript errors occur
 * - Component rendering fails
 * - Lifecycle methods throw errors
 * - Event handlers throw uncaught errors
 *
 * @see App.jsx - Error Boundary implementation
 * @see src/ui/Button.tsx - Retry button component
 * @see src/ui/Heading.tsx - Heading component
 */

import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Heading from './Heading';
import GlobalStyles from '../styles/GlobalStyles';
import Button from './Button';
import IconArrowLeft from '../icons/IconArrowLeft';

/**
 * ErrorFallback props interface
 *
 * WHY error and resetErrorBoundary:
 * - Standard React Error Boundary props pattern
 * - error: Caught error object for context-aware messaging
 * - resetErrorBoundary: Function to attempt recovery
 */
export interface ErrorFallbackProps {
  /** Error object caught by Error Boundary */
  error: Error;

  /** Function to reset Error Boundary and retry */
  resetErrorBoundary: () => void;
}

/**
 * Full-page error container
 *
 * WHY dark background (#1a1c21):
 * - Matches app's dark theme (sports streaming aesthetic)
 * - Softer on eyes than white background
 * - Maintains visual continuity with rest of app
 */
const StyledErrorFallback = styled.main`
  height: 100vh;
  background-color: #1a1c21;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4.8rem;
  position: relative;
  color: #fff;
`;

/**
 * Back button positioned in top-left corner
 *
 * WHY fixed position:
 * - Always visible regardless of content height
 * - Matches AccountManagementLayout pattern for UX consistency
 * - Provides familiar escape hatch for users
 */
const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease-in-out;
  padding: 0;
  margin: 0;
  position: fixed;
  top: 32px;
  left: 32px;
  z-index: 1000;

  &:hover {
    color: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }

  @media screen and (max-width: 767px) {
    top: 16px;
    left: 16px;
  }
`;

/**
 * Styled retry button with theme integration
 *
 * WHY extend Button component:
 * - Consistent styling with rest of app
 * - Theme-aware primary action color
 * - Hover effects for interactivity feedback
 */
const RetryButton = styled(Button)`
  background-color: var(--theme-action-primary);
  font-size: 14px;
  line-height: 22.4px;
  font-weight: 400;
  padding: 12px 24px;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.12) 0%,
        rgba(255, 255, 255, 0.12) 100%
      ),
      var(--theme-action-primary);
  }
`;

/**
 * Content container with vertical layout
 */
const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: var(--font-weight-400);
`;

/**
 * Header section with icon and title
 */
const HeaderBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  font-weight: 600;
`;

/**
 * Text content container with max-width constraint
 */
const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  max-width: 403px;
`;

/**
 * Error message text with responsive sizing
 */
const ErrorText = styled.p`
  text-align: center;
  font-weight: 300;
  max-width: 480px;
  font-size: 16px;
  line-height: 24px;
  margin: 0;
  color: rgba(255, 255, 255, 0.7);

  @media screen and (max-width: 767px) {
    font-size: 14px;
    line-height: 20px;
    max-width: 320px;
  }
`;

/**
 * ErrorFallback component
 *
 * WHY context-aware retry button:
 * - Payment errors are common and specific
 * - "Retry payment" is clearer than generic "Retry"
 * - Better UX for e-commerce context
 *
 * @param props - ErrorFallbackProps
 */
function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps): JSX.Element {
  const navigate = useNavigate();

  // WHY: Determine button text based on error context
  // Payment errors get specific "Retry payment" text for clarity
  const isPaymentError =
    error?.message?.toLowerCase().includes('payment') ||
    error?.message?.toLowerCase().includes('stripe') ||
    error?.message?.toLowerCase().includes('checkout') ||
    error?.name === 'PaymentError';

  const buttonText = isPaymentError ? 'Retry payment' : 'Retry';

  const handleBackClick = () => {
    navigate('/');
  };

  return (
    <>
      <GlobalStyles />
      <StyledErrorFallback>
        <BackButton onClick={handleBackClick} aria-label="Go back to home">
          <IconArrowLeft />
        </BackButton>
        <Content>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="33"
            viewBox="0 0 32 33"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M28.9731 24.5L18.3064 5.83331C18.0738 5.42291 17.7366 5.08156 17.329 4.84407C16.9214 4.60657 16.4581 4.48145 15.9864 4.48145C15.5147 4.48145 15.0514 4.60657 14.6439 4.84407C14.2363 5.08156 13.899 5.42291 13.6664 5.83331L2.99975 24.5C2.76466 24.9071 2.64139 25.3692 2.64243 25.8393C2.64347 26.3095 2.76879 26.771 3.00568 27.1771C3.24257 27.5832 3.58262 27.9194 3.99135 28.1517C4.40008 28.3841 4.86296 28.5042 5.33309 28.5H26.6664C27.1343 28.4995 27.5938 28.3759 27.9988 28.1417C28.4038 27.9074 28.74 27.5708 28.9738 27.1655C29.2075 26.7602 29.3305 26.3005 29.3304 25.8326C29.3302 25.3648 29.207 24.9052 28.9731 24.5Z"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.9998 12.5V17.8333"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.9998 23.1667H16.0132"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <TextBox>
            <HeaderBox>
              <Heading as="h3">An error has occurred</Heading>
            </HeaderBox>
            <ErrorText>
              We're sorry, an unexpected error has occurred. If the issue
              continues to persist, please get in touch with our support team
              who will be able to assist you in resolving the issue.
            </ErrorText>
          </TextBox>

          <RetryButton onClick={resetErrorBoundary}>
            {buttonText}
          </RetryButton>
        </Content>
      </StyledErrorFallback>
    </>
  );
}

export default ErrorFallback;
